import { FC, useState } from "react";
import { AppThemeFontProperty } from "../Types";
import { Box, Card, CardContent, Chip, IconButton, Popover, Typography } from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import FontPopover from "../Popovers/FontPopover";
import { hexToRgba } from "../Helpers";
import { useAppThemeContext } from "src/contexts/AppThemeContext";

type FontPropertyCardProps = {
  property: AppThemeFontProperty;
}

const FontPropertyCard: FC<FontPropertyCardProps> = ({ property }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { masterOptions } = useAppThemeContext();
    
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const open = Boolean(anchorEl);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          transition: "all 0.2s ease-in-out",
          "&:hover": { boxShadow: 1, transform: "translateY(-1px)" },
        }}
      >
        <CardContent
          sx={{
            py: 1.5,
            px: 2,
            "&:last-child": { pb: 1.5 },
            "&:first-child": { pt: 1.5 },
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Box height="100%">
              <Typography variant="subtitle2" fontWeight={600} color="primary">
                {property.label}
              </Typography>
              <Box display="flex" flexDirection="column">
                {/* Row 1: Color & Opacity */}
                <Box display="flex" flexDirection="row" flexWrap="wrap">
                  {property.colour !== undefined && (
                    <Chip
                      label={`Color: ${property.colour}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mt: 0.5, mr: 0.5 }}
                    />
                  )}
                  {property.alpha !== undefined && (
                    <Chip
                      label={`Opacity: ${property.alpha ?? 1}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mt: 0.5 }}
                    />
                  )}
                </Box>
                
                {/* Row 2: Family, Size, Weight */}
                <Box display="flex" flexDirection="row" flexWrap="wrap" mt={0.5}>
                  {property.family !== undefined && (
                    <Chip
                      label={`Family: ${masterOptions?.fontFamilies?.find(x => x.Value === property.family)?.Label ?? ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mr: 0.5, mt: 0.5 }}
                    />
                  )}
                  {property.size !== undefined && (
                    <Chip
                      label={`Size: ${property.size ?? ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mr: 0.5, mt: 0.5 }}
                    />
                  )}
                  {property.weight !== undefined && (
                    <Chip
                      label={`Weight: ${property.weight ?? ''}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.75rem", mt: 0.5 }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  backgroundColor: hexToRgba(property.colour, property.alpha ?? 1),
                  border: "1px solid #ccc",
                }}
              />
              <IconButton size="small" onClick={handleOpen}>
                <Edit fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 1,
            width: "min-content",
            maxWidth: 240
          },
        }}
      >
        <FontPopover property={property} />
      </Popover>
    </>
  )
};

export default FontPropertyCard;